/**
 * 3D Viewer for Print-on-Demand
 * Handles all THREE.js scene setup and 3D rendering functionality
 */

class POD3DViewer {
    constructor(canvasId) {
      this.canvas = document.getElementById(canvasId);
      this.loaderDiv = document.getElementById('loader');
      this.mesh = null;
      
      // Initialize the scene
      this.init();
    }
    
    init() {
      // Initialize renderer
      this.renderer = new THREE.WebGLRenderer({ 
        canvas: this.canvas, 
        alpha: true, 
        antialias: true,
        preserveDrawingBuffer: true,
        powerPreference: "high-performance"
      });
      
      const pixelRatio = window.devicePixelRatio;
      this.renderer.setPixelRatio(pixelRatio);
      this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight, false);
      
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      
      // Store a reference to this viewer instance on the renderer
      this.renderer.pod3dViewer = this;
      
      // Create scene
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf5f5f5);
      
      // Add print bed
      this.addPrintBed();
      
      // Add axes helper
      this.addAxesHelper();
      
      // Add build volume bounding box
      this.addBuildVolumeBoundingBox();
      
      // Setup camera
      this.camera = new THREE.PerspectiveCamera(
        65,
        this.canvas.clientWidth / this.canvas.clientHeight,
        0.1,
        1000
      );
      
      // Set initial camera position to view the print bed
      this.camera.position.set(200, 150, 200);
      this.camera.lookAt(0, 0, 0);
      
      // Add lighting
      this.setupLighting();
      
      // Add orbit controls
      this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
      this.setupControls();
      
      // Add window resize handler
      window.addEventListener('resize', () => this.handleResize());
      
      // Make objects accessible to the window for external scripts
      window.scene = this.scene;
      window.rendererRef = this.renderer;
      window.cameraRef = this.camera;
      window.controlsRef = this.controls;
      window.pod3dViewer = this; // Add a global reference to this viewer instance
      
      // Add model centering function to window for external use
      window.centerModelOnBed = this.centerModelOnBed.bind(this);
      
      // Add UI overlay for model controls and status
      this.createUIOverlay();
      
      // Start animation loop
      this.animate();
    }
  
    // Create UI overlay for model controls and status
    createUIOverlay() {
      const container = document.querySelector('.stl-viewer-container');
      if (!container) return;
      
      // Remove any existing UI
      const existingUI = container.querySelector('.model-controls-ui');
      if (existingUI) existingUI.remove();
      
      // Create main UI container - no flex direction
      const uiOverlay = document.createElement('div');
      uiOverlay.className = 'model-controls-ui';
      
      // Initially hide the UI until a model is loaded
      uiOverlay.style.display = 'none';
      
      // Create top left container for status and dimensions
      const topLeftContainer = document.createElement('div');
      topLeftContainer.className = 'top-left-container';
      
      // Support status will be added dynamically by updateSupportStatusIndicator
      // Create an empty container for it
      const statusContainer = document.createElement('div');
      statusContainer.className = 'status-container';
      topLeftContainer.appendChild(statusContainer);
      
      // Create dimensions display
      const dimensionsDisplay = document.createElement('div');
      dimensionsDisplay.className = 'dimensions-display';
      dimensionsDisplay.id = 'dimensions-display';
      dimensionsDisplay.innerHTML = 'No model loaded';
      topLeftContainer.appendChild(dimensionsDisplay);
      
      uiOverlay.appendChild(topLeftContainer);
      
      // Create top controls container (reset and overhang buttons)
      const topControls = document.createElement('div');
      topControls.className = 'top-controls';
      
      // Reset orientation button
      const resetBtn = document.createElement('button');
      resetBtn.className = 'model-control-btn reset-btn';
      resetBtn.title = 'Reset Model Orientation';
      resetBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
          <path fill="none" stroke="#8BC34A" stroke-width="2" d="M3,12c0,4.97,4.03,9,9,9s9-4.03,9-9s-4.03-9-9-9c-2.56,0-4.87,1.07-6.5,2.78L4,7"/>
          <path fill="#8BC34A" d="M6,3L1,7l5,4V3z"/>
        </svg>
      `;
      resetBtn.addEventListener('click', () => {
        if (typeof STLWeightEstimator !== 'undefined' && STLWeightEstimator.resetModelOrientation) {
          // Use the original reset function which already handles realignment properly
          STLWeightEstimator.resetModelOrientation();
          this.updateSupportStatusIndicator();
          this.updateDimensionsDisplay();
          
          // Reset scale slider to 1.0
          document.getElementById('scale-slider').value = '1';
          document.getElementById('scale-value').textContent = '1.00×';
          this.scaleModel(1);
          
          // Ensure pendingRealignment is cleared
          window.pendingRealignment = false;
          if (window.realignTimer) {
            clearTimeout(window.realignTimer);
            window.realignTimer = null;
          }
          
          // Reset overhang visualization button state
          const overhangBtn = document.querySelector('.overhang-btn');
          if (overhangBtn) {
            overhangBtn.classList.remove('active');
          }
          
          // Remove any existing overhang visualization
          if (window.overhangVisualization) {
            window.pivotGroup.remove(window.overhangVisualization);
            window.overhangVisualization.geometry.dispose();
            window.overhangVisualization.material.dispose();
            window.overhangVisualization = null;
          }
        }
      });
      topControls.appendChild(resetBtn);
      
      // Show/hide overhangs button
      const overhangBtn = document.createElement('button');
      overhangBtn.className = 'model-control-btn overhang-btn';
      overhangBtn.title = 'Show/Hide Overhangs';
      overhangBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
          <path fill="#8BC34A" d="M12,4C4.27,4,0,12,0,12s4.27,8,12,8s12-8,12-8S19.73,4,12,4z M12,17c-2.76,0-5-2.24-5-5s2.24-5,5-5s5,2.24,5,5S14.76,17,12,17z"/>
          <circle cx="12" cy="12" r="3" fill="#8BC34A"/>
        </svg>
      `;
      overhangBtn.addEventListener('click', () => {
        if (typeof STLWeightEstimator !== 'undefined' && STLWeightEstimator.toggleOverhangVisualization) {
          STLWeightEstimator.toggleOverhangVisualization();
          
          // Toggle active state
          overhangBtn.classList.toggle('active');
        }
      });
      topControls.appendChild(overhangBtn);
      
      // Add top controls to main UI
      uiOverlay.appendChild(topControls);
      
      // Create bottom left container for rotation controls
      const bottomLeftControls = document.createElement('div');
      bottomLeftControls.className = 'bottom-left-controls';
      
      // Create rotation controls container
      const rotationControlsContainer = document.createElement('div');
      rotationControlsContainer.className = 'rotation-controls';
      
      // Rotation buttons
      const axes = ['X', 'Y', 'Z'];
      const axisColors = ['#ff5252', '#4caf50', '#2196f3'];
      
      axes.forEach((axis, index) => {
        const rotationGroup = document.createElement('div');
        rotationGroup.className = 'rotation-group';
        
        // Label
        const axisLabel = document.createElement('div');
        axisLabel.className = 'axis-label';
        axisLabel.textContent = axis;
        axisLabel.style.backgroundColor = axisColors[index] + '20'; // Add transparency
        axisLabel.style.color = axisColors[index];
        rotationGroup.appendChild(axisLabel);
        
        // Button container
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'axis-buttons';
        
        // CCW button
        const ccwBtn = document.createElement('button');
        ccwBtn.className = 'model-control-btn rotation-btn';
        ccwBtn.title = `Rotate ${axis} 90° CCW`;
        ccwBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
            <path fill="${axisColors[index]}" d="M12,5V1L7,6l5,5V7c3.31,0,6,2.69,6,6s-2.69,6-6,6s-6-2.69-6-6H4c0,4.42,3.58,8,8,8s8-3.58,8-8S16.42,5,12,5z" transform="scale(-1, 1) translate(-24, 0)"/>
          </svg>
        `;
        ccwBtn.addEventListener('click', () => {
          if (typeof STLWeightEstimator !== 'undefined' && STLWeightEstimator.rotateModel) {
            STLWeightEstimator.rotateModel(axis, -90);
            this.updateSupportStatusIndicator();
            this.updateDimensionsDisplay();
          }
        });
        buttonContainer.appendChild(ccwBtn);
        
        // CW button
        const cwBtn = document.createElement('button');
        cwBtn.className = 'model-control-btn rotation-btn';
        cwBtn.title = `Rotate ${axis} 90° CW`;
        cwBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
            <path fill="${axisColors[index]}" d="M12,5V1L7,6l5,5V7c3.31,0,6,2.69,6,6s-2.69,6-6,6s-6-2.69-6-6H4c0,4.42,3.58,8,8,8s8-3.58,8-8S16.42,5,12,5z"/>
          </svg>
        `;
        cwBtn.addEventListener('click', () => {
          if (typeof STLWeightEstimator !== 'undefined' && STLWeightEstimator.rotateModel) {
            STLWeightEstimator.rotateModel(axis, 90);
            this.updateSupportStatusIndicator();
            this.updateDimensionsDisplay();
          }
        });
        buttonContainer.appendChild(cwBtn);
        
        rotationGroup.appendChild(buttonContainer);
        rotationControlsContainer.appendChild(rotationGroup);
      });
      
      bottomLeftControls.appendChild(rotationControlsContainer);
      
      // Create scale control container for bottom middle
      const bottomScaleContainer = document.createElement('div');
      bottomScaleContainer.className = 'bottom-scale-container';
      
      // Create scale container
      const scaleContainer = document.createElement('div');
      scaleContainer.className = 'scale-container';
      
      // Create scale slider
      const scaleSlider = document.createElement('input');
      scaleSlider.type = 'range';
      scaleSlider.min = '0.1';
      scaleSlider.max = '3';
      scaleSlider.step = '0.05';
      scaleSlider.value = '1';
      scaleSlider.className = 'scale-slider';
      scaleSlider.id = 'scale-slider';
      
      // Create scale value display
      const scaleValue = document.createElement('span');
      scaleValue.className = 'scale-value';
      scaleValue.id = 'scale-value';
      scaleValue.textContent = '1.00×';
      
      // Add event listener for scaling
      let scaleDebounceTimer;
      
      // Handle input (sliding) with immediate visual feedback, but delayed calculations
      scaleSlider.addEventListener('input', () => {
        const scale = parseFloat(scaleSlider.value);
        scaleValue.textContent = scale.toFixed(2) + '×';
        
        // Apply only the visual scale without running any calculations
        this.applyVisualScaleOnly(scale);
        
        // Clear previous timer if exists
        if (scaleDebounceTimer) clearTimeout(scaleDebounceTimer);
        
        // Set a new timer to update support calculations after delay
        scaleDebounceTimer = setTimeout(() => {
          // Run full scale update with all calculations
          this.completeScaleUpdate(scale);
        }, 800); // Increased from 500ms to 800ms for smoother experience
      });
      
      // Handle change (release) event
      scaleSlider.addEventListener('change', () => {
        const scale = parseFloat(scaleSlider.value);
        
        // If there's a pending debounce, clear it and run immediately
        if (scaleDebounceTimer) {
          clearTimeout(scaleDebounceTimer);
          scaleDebounceTimer = null;
        }
        
        // Complete the scale operation with full calculations
        this.completeScaleUpdate(scale);
      });
      
      // Add elements to container
      scaleContainer.appendChild(scaleSlider);
      scaleContainer.appendChild(scaleValue);
      bottomScaleContainer.appendChild(scaleContainer);
      
      uiOverlay.appendChild(bottomLeftControls);
      uiOverlay.appendChild(bottomScaleContainer);
      
      // Store the UI overlay reference and add it to the container
      this.uiOverlay = uiOverlay;
      container.appendChild(uiOverlay);
      
      // Add CSS for the UI
      this.addUIStyles();
    }
    
    // Add dynamic CSS styles for the 3D viewer UI
    addUIStyles() {
      // Check if styles already exist
      if (document.getElementById('pod-3d-viewer-styles')) return;
      
      const styleElement = document.createElement('style');
      styleElement.id = 'pod-3d-viewer-styles';
      
      styleElement.textContent = `
        .model-controls-ui {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 48px;
          pointer-events: none;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 10px;
          z-index: 10;
        }
        
        .top-left-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
          margin: 10px;
        }
        
        .status-container {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        
        .dimensions-display {
          background-color: rgba(255, 255, 255, 0.8);
          color: #333;
          padding: 5px 10px;
          border-radius: 4px;
          font-family: sans-serif;
          font-size: 1.0rem;
          text-align: center;
          pointer-events: none;
        }
        
        .top-controls {
          position: absolute;
          top: 10px;
          right: 10px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          pointer-events: none;
        }
        
        .model-control-btn {
          background-color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
          transition: background-color 0.2s, transform 0.2s;
        }
        
        .model-control-btn:hover {
          background-color: #f5f5f5;
          transform: scale(1.05);
        }
        
        .model-control-btn.active {
          background-color: #8BC34A20;
        }
        
        .bottom-left-controls {
          position: absolute;
          bottom: 20px;
          left: 10px;
          background: rgba(255, 255, 255, 0.8);
          padding: 12px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          pointer-events: auto;
          display: inline-flex;
          flex-direction: column;
          min-width: fit-content;
        }
        
        .rotation-controls {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 0;
        }
        
        .rotation-group {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .axis-label {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-weight: bold;
          font-family: sans-serif;
          font-size: 1.0rem;
        }
        
        .control-label {
          font-size: 1.0rem;
          font-weight: bold;
          margin-bottom: 8px;
          color: #666;
        }
        
        .axis-buttons {
          display: flex;
          gap: 8px;
        }
        
        .rotation-btn {
          background-color: white;
          border: none;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
          transition: background-color 0.2s;
        }
        
        .rotation-btn:hover {
          background-color: #f5f5f5;
        }
        
        .support-status {
          display: flex;
          align-items: center;
          gap: 8px;
          background-color: white;
          border-radius: 6px;
          padding: 8px 12px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.15);
          font-family: sans-serif;
          pointer-events: auto;
        }
        
        .support-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .support-icon.ready {
          background-color: #8BC34A;
          color: white;
        }
        
        .support-icon.warning {
          background-color: #FF9800;
          color: white;
        }
        
        .support-icon.error {
          background-color: #FF5252;
          color: white;
        }
        
        .support-text {
          font-size: 1.2rem;
          color: #333;
        }
        
        .bottom-scale-container {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          justify-content: center;
          pointer-events: none;
        }
        
        .scale-container {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 8px;
          padding: 6px 10px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          backdrop-filter: blur(4px);
          pointer-events: auto;
        }
        
        .scale-slider {
          -webkit-appearance: none;
          width: 80%;
          height: 3px;
          border-radius: 2px;
          background: #5a5a5a;
          outline: none;
          margin-right: 10px;
          /* Make the track non-interactive */
          pointer-events: none;
        }
        
        .scale-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #8BC34A;
          cursor: grab;
          /* Re-enable pointer events only for the thumb */
          pointer-events: auto;
        }
        
        .scale-slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #8BC34A;
          cursor: grab;
          border: none;
          /* Re-enable pointer events only for the thumb */
          pointer-events: auto;
        }
        
        .scale-value {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          min-width: 42px;
          text-align: right;
        }
      `;
      
      document.head.appendChild(styleElement);
    }
    
    // Update the support status indicator
    updateSupportStatusIndicator() {
      if (!window.mesh || typeof STLWeightEstimator === 'undefined') return;
      
      // Remove any existing indicator
      const existingIndicator = this.uiOverlay.querySelector('.support-status');
      if (existingIndicator) existingIndicator.remove();
      
      // Check dimensions first - this takes priority over support requirements
      const bbox = new THREE.Box3().setFromObject(window.mesh);
      const size = bbox.getSize(new THREE.Vector3());
      const MAX_SIZE = 250; // 250mm in any dimension
      const exceedsSize = size.x > MAX_SIZE || size.y > MAX_SIZE || size.z > MAX_SIZE;
      
      // Get support requirements (only if size doesn't exceed max)
      // Always calculate fresh support requirements rather than using stored value
      const requiresSupports = !exceedsSize && typeof STLWeightEstimator.analyzeCurrentSupportRequirements === 'function'
        ? STLWeightEstimator.analyzeCurrentSupportRequirements() 
        : false;
      
      // Create indicator  
      const indicator = document.createElement('div');
      indicator.className = 'support-status';
      
      const iconDiv = document.createElement('div');
      
      // If size exceeds max, show size warning instead of support status
      if (exceedsSize) {
        iconDiv.className = 'support-icon error';
        iconDiv.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>';
        
        const textDiv = document.createElement('div');
        textDiv.className = 'support-text';
        textDiv.textContent = 'Size exceeds 250mm limit';
        
        indicator.appendChild(iconDiv);
        indicator.appendChild(textDiv);
        
        // Update bounding box color
        this.updateBoundingBoxColor(true);
        
        // Store size exceeded flag
        window.sizeExceeded = true;
      } else {
        // Regular support status
        iconDiv.className = `support-icon ${requiresSupports ? 'warning' : 'ready'}`;
        
        // Update icon color to match project green
        iconDiv.innerHTML = requiresSupports 
          ? '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/></svg>'
          : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>';
        
        const textDiv = document.createElement('div');
        textDiv.className = 'support-text';
        textDiv.textContent = requiresSupports 
          ? 'Supports recommended'
          : 'Print ready';
        
        indicator.appendChild(iconDiv);
        indicator.appendChild(textDiv);
        
        // Update bounding box color
        this.updateBoundingBoxColor(false);
        
        // Clear size exceeded flag
        window.sizeExceeded = false;
      }
      
      // Add to status container
      const statusContainer = this.uiOverlay.querySelector('.status-container');
      if (statusContainer) {
        statusContainer.appendChild(indicator);
      }
      
      // Store the latest support requirement for other components to use
      window.latestSupportRequirement = requiresSupports;
    }
  
    // Update bounding box color based on dimension check
    updateBoundingBoxColor(exceedsSize) {
      if (!window.buildVolumeBoundingBox) return;
      
      // Get the line material
      const material = window.buildVolumeBoundingBox.material;
      
      // Update the color based on size check
      if (exceedsSize) {
        material.color.set(0xFF5252); // Red color for exceeding size
        material.opacity = 0.8; // Make it more visible
      } else {
        material.color.set(0x8BC34A); // Green color for acceptable size
        material.opacity = 0.7; // Regular opacity
      }
      
      // Ensure the renderer updates
      if (window.rendererRef) {
        window.rendererRef.render(window.scene, window.cameraRef);
      }
    }
  
    // Update dimensions display with current model measurements
    updateDimensionsDisplay() {
      const dimensionsDisplay = document.getElementById('dimensions-display');
      if (!dimensionsDisplay || !window.mesh) return;
      
      // Calculate model dimensions
      const boundingBox = new THREE.Box3().setFromObject(window.mesh);
      const size = boundingBox.getSize(new THREE.Vector3());
      
      // Format dimensions with one decimal place
      const width = size.x.toFixed(1);
      const height = size.y.toFixed(1);
      const depth = size.z.toFixed(1);
      
      // Show dimensions with L, W, H labels
      dimensionsDisplay.innerHTML = `Size: <span title="Length">L: ${width}</span> × <span title="Width">W: ${depth}</span> × <span title="Height">H: ${height}</span> mm`;
      
      // Check if any dimension exceeds maximum size
      const MAX_DIMENSION = 250; // mm
      if (size.x > MAX_DIMENSION || size.y > MAX_DIMENSION || size.z > MAX_DIMENSION) {
        dimensionsDisplay.style.color = '#f44336'; // Red color
        dimensionsDisplay.style.fontWeight = 'bold';
      } else {
        dimensionsDisplay.style.color = '#333'; // Regular color
        dimensionsDisplay.style.fontWeight = 'normal';
      }
      
      // Call support status update when dimensions change
      this.updateSupportStatusIndicator();
    }
  
    addPrintBed() {
      const gridSize = 250; // 250mm x 250mm print bed (standard size)
      const gridDivisions = 25; // 10mm grid lines
      
      // Create print bed at y=0 (bottom of build volume)
      const planeGeometry = new THREE.PlaneGeometry(gridSize, gridSize);
      const planeMaterial = new THREE.MeshBasicMaterial({ 
        color: 0xf0f0f0, 
        transparent: true, 
        opacity: 0.85,
        side: THREE.DoubleSide
      });
      const plane = new THREE.Mesh(planeGeometry, planeMaterial);
      plane.rotation.x = Math.PI / 2; // Rotate to XZ plane
      plane.position.y = -0.05; // Position at y=0 (bottom of build volume)
      this.scene.add(plane);
      
      // Create grid helper at y=0 (at print bed level) rather than at y=125
      const gridHelper = new THREE.GridHelper(gridSize, gridDivisions, 0x888888, 0xcccccc);
      // No rotation needed as GridHelper is already in the XZ plane
      gridHelper.position.y = -1; // Position at y=-1 (just below print bed) to avoid z-fighting
      gridHelper.position.z = 0; // Center the grid
      this.scene.add(gridHelper);
      
      // Store references for later visibility toggling
      window.printBedGrid = gridHelper;
      window.printBedPlane = plane;
    }
  
    addGridMeasurements() {
      // Clear any previous labels
      document.querySelectorAll('.grid-measurement').forEach(el => el.remove());
      
      // No additional grid measurements needed for the new UI
    }
  
    addAxesHelper() {
      const axesHelper = new THREE.AxesHelper(20); // size of the axes (20mm)
      
      // Position the axes helper at the corner of the print bed
      axesHelper.position.set(-120, 0, -120); // offset from center of the 250x250 print bed
      
      // Add a small sphere at the origin of the axes
      const originSphere = new THREE.Mesh(
        new THREE.SphereGeometry(2, 16, 16),
        new THREE.MeshBasicMaterial({ color: 0xffffff })
      );
      originSphere.position.copy(axesHelper.position);
      
      this.scene.add(axesHelper);
      this.scene.add(originSphere);
      
      // Store reference for later
      window.axesHelper = axesHelper;
      window.axesOrigin = originSphere;
    }
  
    setupLighting() {
      const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.4);
      this.scene.add(hemiLight);
  
      const mainLight = new THREE.DirectionalLight(0xffffff, 0.5);
      mainLight.position.set(1, 2, 1);
      this.scene.add(mainLight);
  
      const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
      fillLight.position.set(-1, 1, -1);
      this.scene.add(fillLight);
  
      const rimLight = new THREE.DirectionalLight(0xffffff, 0.2);
      rimLight.position.set(0, -1, 0);
      this.scene.add(rimLight);
    }
  
    setupControls() {
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.screenSpacePanning = true;
      
      // Add zoom limits to prevent zooming too far in/out
      this.controls.minDistance = 20; // Prevent zooming too close
      this.controls.maxDistance = 600; // Prevent zooming too far
      
      // Limit vertical orbit to prevent flipping
      this.controls.minPolarAngle = 0; // Radians - top view
      this.controls.maxPolarAngle = Math.PI / 2; // Radians - horizon view
      
      // Add real-time support calculation during rotation
      let lastUpdateTime = 0;
      const UPDATE_INTERVAL = 500; // 500ms interval to limit performance impact
      
      this.controls.addEventListener('change', () => {
        if (!window.mesh || !window.pivotGroup) return;
        
        const now = Date.now();
        if (now - lastUpdateTime > UPDATE_INTERVAL) {
          lastUpdateTime = now;
          
          // Update the support status based on current orientation
          if (typeof STLWeightEstimator !== 'undefined' && 
              typeof STLWeightEstimator.analyzeCurrentSupportRequirements === 'function') {
            const supportRequired = STLWeightEstimator.analyzeCurrentSupportRequirements();
            STLWeightEstimator.trackSupportRequirements(supportRequired);
            this.updateSupportStatusIndicator();
          }
        }
      });
    }
  
    handleResize() {
      const width = this.canvas.clientWidth;
      const height = this.canvas.clientHeight;
      const pixelRatio = window.devicePixelRatio;
      
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      
      this.renderer.setSize(width, height, false);
      this.renderer.setPixelRatio(pixelRatio);
    }
  
    animate() {
      requestAnimationFrame(() => this.animate());
      this.controls.update();
      
      this.renderer.render(this.scene, this.camera);
    }
    
    centerModelOnBed(obj) {
      if (!obj) return;
      
      const targetMesh = obj === window.pivotGroup ? window.mesh : obj;
      const targetObj = obj === window.pivotGroup ? window.pivotGroup : obj;
      
      // Get the bounding box of the model
      // Reuse existing THREE.Box3 and Vector3 objects to reduce allocations
      const bbox = this._tempBox || new THREE.Box3();
      this._tempBox = bbox;
      
      const center = this._tempVector || new THREE.Vector3();
      this._tempVector = center;
      
      // Calculate bounding box and center
      bbox.setFromObject(targetObj);
      bbox.getCenter(center);
      const size = bbox.getSize(this._tempSize || new THREE.Vector3());
      this._tempSize = size;
      
      // Find the minimum Y value of the bounding box (bottom face)
      const bottomY = bbox.min.y;
      
      // Move the object to sit on the print bed (y=0)
      if (obj === window.pivotGroup) {
        // When using pivot group, we need to adjust the group position
        // to ensure the bottom of the model is at y=0
        window.pivotGroup.position.y -= bottomY; // Align bottom face exactly at y=0
        
        // Center on X and Z axes
        window.pivotGroup.position.x = -center.x + window.pivotGroup.position.x;
        window.pivotGroup.position.z = -center.z + window.pivotGroup.position.z;
      } else {
        // Direct positioning when not using pivot
        const yOffset = -bottomY; // Offset to align bottom face with print bed (y=0)
        
        // Position the model with bottom at y=0
        obj.position.y = yOffset;
        
        // Center model on the print bed (x=0, z=0)
        obj.position.x = -center.x;
        obj.position.z = -center.z;
      }
      
      // Update matrices
      if (targetMesh.updateMatrix) targetMesh.updateMatrix();
      if (targetObj.updateMatrix) targetObj.updateMatrix();
    }
  
    loadSTLFile(file) {
      return new Promise((resolve, reject) => {
        if (!file) {
          reject(new Error('No file provided'));
          return;
        }
        
        // Reset scale slider to 1.0
        document.getElementById('scale-slider').value = '1';
        document.getElementById('scale-value').textContent = '1.00×';
        this.scaleModel(1);
        
        // Reset overhang visualization
        if (window.overhangVisualization) {
          if (window.pivotGroup) {
            window.pivotGroup.remove(window.overhangVisualization);
          } else {
            this.scene.remove(window.overhangVisualization);
          }
          window.overhangVisualization.geometry.dispose();
          window.overhangVisualization.material.dispose();
          window.overhangVisualization = null;
          
          // Reset active state on button if it exists
          const overhangBtn = document.querySelector('.overhang-btn');
          if (overhangBtn) {
            overhangBtn.classList.remove('active');
          }
        }
        
        this.loaderDiv.style.display = 'flex';
        document.getElementById('empty-canvas-message').style.display = 'none';
        
        // Clean up previous model before loading a new one
        this.cleanupPreviousModel();
        
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const loader = new THREE.STLLoader();
            const geometry = loader.parse(e.target.result);
            
            // Check size before loading
            const geometryBBox = new THREE.Box3().setFromObject(new THREE.Mesh(geometry));
            const size = geometryBBox.getSize(new THREE.Vector3());
            const MAX_SIZE = 250; // 250mm in any dimension
            
            const selectedMaterialType = document.getElementById('selectedMaterial').value;
            const selectedColor = document.getElementById('customSelectedColor').value;
            
            // Create material using our internal function
            const material = pod3dCreateMaterial(selectedMaterialType, selectedColor);
            this.mesh = new THREE.Mesh(geometry, material);
            this.scene.add(this.mesh);
            
            // Make mesh available to window for orientation tools
            window.mesh = this.mesh;
            window.controls = this.controls;
            
            // Initialize the pivot group for rotations - this will also center on print bed
            if (typeof STLWeightEstimator !== 'undefined' && 
                typeof STLWeightEstimator.initializePivotGroup === 'function') {
              STLWeightEstimator.initializePivotGroup(this.mesh);
              
              // Center and frame the model in view
              const box = new THREE.Box3().setFromObject(window.pivotGroup);
              const center = box.getCenter(new THREE.Vector3());
              const size = box.getSize(new THREE.Vector3());
              const maxDim = Math.max(size.x, size.y, size.z);
              const fov = this.camera.fov * (Math.PI / 180);
              const cameraZ = maxDim / (2 * Math.tan(fov / 2));
              
              // Position camera to show both model and print bed
              this.camera.position.set(
                center.x + maxDim, 
                center.y + maxDim * 0.8, // Higher position to see both bed and grid
                center.z + cameraZ * 1.5
              );
              this.camera.lookAt(center);
              this.controls.target.copy(center);
              this.controls.update();
              
              // Update support status indicator
              setTimeout(() => {
                this.updateSupportStatusIndicator();
                this.updateDimensionsDisplay();
                
                // Update PrintDetailsHandler with dimensions
                this.updatePrintDetailsWithCurrentDimensions();
                
                // Show the UI controls now that a model is loaded
                if (this.uiOverlay) {
                  this.uiOverlay.style.display = 'flex';
                }
              }, 100);
            } else {
              // If STLWeightEstimator isn't available, do basic centering
              this.centerModelOnBed(this.mesh);
              
              // Basic camera positioning - adjust to see grid at y=125
              const bbox = new THREE.Box3().setFromObject(this.mesh);
              const center = bbox.getCenter(new THREE.Vector3());
              const size = bbox.getSize(new THREE.Vector3());
              const maxDim = Math.max(size.x, size.y, size.z);
              const fov = this.camera.fov * (Math.PI / 180);
              const cameraZ = maxDim / (2 * Math.tan(fov / 2));
              
              // Adjusted camera position
              this.camera.position.set(
                center.x + maxDim, 
                center.y + maxDim * 0.8, // Higher to see both bed and grid
                center.z + cameraZ * 1.5
              );
              this.camera.lookAt(center);
              this.controls.target.copy(center);
              this.controls.update();
              
              // Show the UI controls now that a model is loaded
              if (this.uiOverlay) {
                this.uiOverlay.style.display = 'flex';
              }
              
              // Update dimensions display
              this.updateDimensionsDisplay();
              
              // Update PrintDetailsHandler with dimensions
              this.updatePrintDetailsWithCurrentDimensions();
            }
            
            this.loaderDiv.style.display = 'none';
            resolve(geometry);
          } catch (error) {
            console.error('Error in STL file loading:', error);
            reject(error);
            this.loaderDiv.style.display = 'none';
          }
        };
        
        reader.onerror = (error) => {
          console.error('File reading error:', error);
          reject(error);
          this.loaderDiv.style.display = 'none';
        };
        
        reader.readAsArrayBuffer(file);
      });
    }
  
    cleanupPreviousModel() {
      // Remove existing mesh
      if (window.mesh) {
        if (window.pivotGroup) {
          window.pivotGroup.remove(window.mesh);
        } else {
          this.scene.remove(window.mesh);
        }
        window.mesh.geometry.dispose();
        window.mesh.material.dispose();
        window.mesh = null;
      }
      
      // Remove pivot group
      if (window.pivotGroup) {
        this.scene.remove(window.pivotGroup);
        window.pivotGroup = null;
      }
      
      // Remove overhang visualization
      if (window.overhangVisualization) {
        this.scene.remove(window.overhangVisualization);
        window.overhangVisualization.geometry.dispose();
        window.overhangVisualization.material.dispose();
        window.overhangVisualization = null;
      }
      
      // Remove build direction arrow
      if (window.buildDirectionArrow) {
        this.scene.remove(window.buildDirectionArrow);
        window.buildDirectionArrow = null;
      }
      
      // Clear original geometry
      if (window.originalGeometry) {
        window.originalGeometry.dispose();
        window.originalGeometry = null;
      }
      
      // Hide the UI controls since no model is loaded
      if (this.uiOverlay) {
        this.uiOverlay.style.display = 'none';
      }
    }
  
    // Add a bounding box representing the build volume (250x250x250mm)
    addBuildVolumeBoundingBox() {
      const buildSize = 250; // 250mm in each dimension
      
      // Create wireframe box geometry
      const boxGeometry = new THREE.BoxGeometry(buildSize, buildSize, buildSize);
      const edges = new THREE.EdgesGeometry(boxGeometry);
      
      // Create green line material
      const lineMaterial = new THREE.LineBasicMaterial({ 
        color: 0x8BC34A, // Green color to match the theme
        linewidth: 1,
        opacity: 0.7,
        transparent: true
      });
      
      // Create wireframe box
      const boundingBox = new THREE.LineSegments(edges, lineMaterial);
      
      // Position the box so its bottom face is at y=0 (print bed level)
      // and center is at (0,125,0)
      boundingBox.position.set(0, buildSize/2, 0);
      
      // Add to scene
      this.scene.add(boundingBox);
      
      // Store reference
      window.buildVolumeBoundingBox = boundingBox;
    }
  
    scaleModel(scaleFactor) {
      if (!window.mesh) return;
      
      // Apply the visual scale first
      this.applyVisualScaleOnly(scaleFactor);
      
      // Then complete the full scale update with calculations
      this.completeScaleUpdate(scaleFactor);
    }
    
    // Apply only visual scaling without updating support calculations (for smoother slider movement)
    applyVisualScaleOnly(scaleFactor) {
      if (!window.mesh) return;
      
      // Store original scale if not already stored
      if (!window.originalScale && scaleFactor !== 1) {
        window.originalScale = {
          x: window.mesh.scale.x,
          y: window.mesh.scale.y,
          z: window.mesh.scale.z
        };
      }
      
      // Apply scaling relative to original scale
      if (window.originalScale) {
        window.mesh.scale.x = window.originalScale.x * scaleFactor;
        window.mesh.scale.y = window.originalScale.y * scaleFactor;
        window.mesh.scale.z = window.originalScale.z * scaleFactor;
      } else {
        window.mesh.scale.set(scaleFactor, scaleFactor, scaleFactor);
      }
      
      // Update the model's matrices
      window.mesh.updateMatrix();
      
      if (window.pivotGroup) {
        window.pivotGroup.updateMatrixWorld(true);
      }
      
      // Center the model while scaling for better visual experience
      this.centerModelOnBed(window.pivotGroup || window.mesh);
      
      // Scale any visualization without recalculating
      if (window.overhangVisualization) {
        window.overhangVisualization.scale.copy(window.mesh.scale);
      }
    }
    
    // Helper method to scale the overhang visualization without recalculating it
    updateOverhangMeshScale() {
      // Check if overhang visualization is active
      if (window.overhangVisualization && window.mesh) {
        // Copy the scale from the main mesh to the visualization
        window.overhangVisualization.scale.copy(window.mesh.scale);
      }
    }
    
    // Helper method to update overhang visualization
    updateOverhangVisualization() {
      // Check if overhang visualization is active
      if (window.overhangVisualization) {
        // Remove existing visualization
        if (window.pivotGroup) {
          window.pivotGroup.remove(window.overhangVisualization);
        } else {
          this.scene.remove(window.overhangVisualization);
        }
        window.overhangVisualization.geometry.dispose();
        window.overhangVisualization.material.dispose();
        window.overhangVisualization = null;
        
        // Recreate visualization with current scale/orientation
        if (typeof STLWeightEstimator !== 'undefined' && STLWeightEstimator.toggleOverhangVisualization) {
          STLWeightEstimator.toggleOverhangVisualization();
        }
      }
      
      // Update support requirement calculation
      if (typeof STLWeightEstimator !== 'undefined' && STLWeightEstimator.analyzeCurrentSupportRequirements) {
        STLWeightEstimator.analyzeCurrentSupportRequirements();
      }
    }
    
    // Update PrintDetailsHandler with current model dimensions
    updatePrintDetailsWithCurrentDimensions() {
      if (!window.mesh || typeof PrintDetailsHandler === 'undefined') return;
      
      // Get current model dimensions
      const bbox = new THREE.Box3().setFromObject(window.mesh);
      const size = bbox.getSize(new THREE.Vector3());
      
      // Round to 1 decimal place
      const width = Math.round(size.x * 10) / 10;
      const height = Math.round(size.y * 10) / 10;
      const depth = Math.round(size.z * 10) / 10;
      
      // Update PrintDetailsHandler
      PrintDetailsHandler.updateDetails({
        dimensions: {
          length: width,
          width: depth,
          height: height
        }
      });
    }
  
    // Complete the scale operation with all necessary calculations
    completeScaleUpdate(scaleFactor) {
      if (!window.mesh) return;
      
      // Model is already centered during visual scaling, no need to recenter
      
      // Update dimensions display with new dimensions
      this.updateDimensionsDisplay();
      
      // Update PrintDetailsHandler with new dimensions
      this.updatePrintDetailsWithCurrentDimensions();
      
      // Run support calculations
      if (typeof STLWeightEstimator !== 'undefined' && STLWeightEstimator.analyzeCurrentSupportRequirements) {
        const supportRequired = STLWeightEstimator.analyzeCurrentSupportRequirements();
        STLWeightEstimator.trackSupportRequirements(supportRequired);
      }
      
      // Update overhang visualization if active
      this.updateOverhangVisualization();
    }
  
    // Lock or unlock specific model interactions (rotation and scaling)
    // while keeping camera movement enabled
    lockModelInteractions(lock) {
      // 1. Handle rotation buttons
      const rotationBtns = document.querySelectorAll('.rotation-btn');
      rotationBtns.forEach(btn => {
        if (lock) {
          btn.disabled = true;
          btn.style.opacity = '0.5';
          btn.style.cursor = 'not-allowed';
        } else {
          btn.disabled = false;
          btn.style.opacity = '1';
          btn.style.cursor = 'pointer';
        }
      });
      
      // 2. Handle scale slider
      const scaleSlider = document.getElementById('scale-slider');
      if (scaleSlider) {
        scaleSlider.disabled = lock;
        scaleSlider.style.opacity = lock ? '0.5' : '1';
        scaleSlider.parentElement.style.cursor = lock ? 'not-allowed' : 'pointer';
      }
      
      // 3. Handle reset button
      const resetBtn = document.querySelector('.reset-btn');
      if (resetBtn) {
        resetBtn.disabled = lock;
        resetBtn.style.opacity = lock ? '0.5' : '1';
        resetBtn.style.cursor = lock ? 'not-allowed' : 'pointer';
      }
      
      // 4. Handle overhang visualization button
      const overhangBtn = document.querySelector('.overhang-btn');
      if (overhangBtn) {
        overhangBtn.disabled = lock;
        overhangBtn.style.opacity = lock ? '0.5' : '1';
        overhangBtn.style.cursor = lock ? 'not-allowed' : 'pointer';
      }
      
      // 5. Keep orbit controls enabled for camera movement
      // We want the user to still be able to orbit around the model
      // but not change the model's orientation directly
      
      // 6. Apply a visual indicator that the model is locked
      if (window.mesh) {
        const container = document.querySelector('.stl-viewer-container');
        
        if (lock) {
          // Add a "View Only" indicator
          if (!document.getElementById('view-only-indicator')) {
            const indicator = document.createElement('div');
            indicator.id = 'view-only-indicator';
            indicator.textContent = 'View Only Mode';
            indicator.style.position = 'absolute';
            indicator.style.top = '10px';
            indicator.style.left = '50%';
            indicator.style.transform = 'translateX(-50%)';
            indicator.style.background = 'rgba(0,0,0,0.6)';
            indicator.style.color = 'white';
            indicator.style.padding = '5px 12px';
            indicator.style.borderRadius = '15px';
            indicator.style.fontSize = '12px';
            indicator.style.fontWeight = 'bold';
            indicator.style.zIndex = '100';
            container.appendChild(indicator);
          }
        } else {
          // Remove the view-only indicator if present
          const indicator = document.getElementById('view-only-indicator');
          if (indicator) {
            indicator.remove();
          }
        }
      }
    }
  
    // Apply scaling without updating support calculations (for better performance)
    applyScaleOnly(scaleFactor) {
      if (!window.mesh) return;
      
      // Apply just the visual scale (which now includes centering)
      this.applyVisualScaleOnly(scaleFactor);
      
      // Update dimensions display
      this.updateDimensionsDisplay();
      
      // Update PrintDetailsHandler
      this.updatePrintDetailsWithCurrentDimensions();
    }
  }
  
  // Helper function to create material for STL models
  function pod3dCreateMaterial(materialType, colorHex) {
    const material = new THREE.MeshPhongMaterial({
      color: new THREE.Color(colorHex),
      shininess: 30,
      specular: 0x111111
    });
    
    // Apply different settings based on material type
    switch(materialType) {
      case 'pla-matte':
        material.flatShading = true;
        material.shininess = 10;
        break;
      case 'petg':
        material.transparent = true;
        material.opacity = 0.9;
        material.shininess = 80;
        break;
      case 'abs':
        material.flatShading = true;
        material.shininess = 20;
        break;
      default: // PLA
        // Default settings already applied
        break;
    }
    
    return material;
  }
  
  // Make pod3dCreateMaterial available globally
  window.pod3dCreateMaterial = pod3dCreateMaterial; 